import type { FC } from 'react';
import React, { Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import Button from '@atlaskit/button/default/button';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { usePresence } from '@confluence/native-collab/entry-points/usePresence.ts';
import { deleteDraft } from '@confluence/editor-features/entry-points/deleteDraft.ts';
import { useLabels } from '@confluence/editor-features/entry-points/useLabels.ts';
import { PreloadButtonWrapper } from '@confluence/editor-features/entry-points/PreloadButtonWrapper.ts';
import { getParentOfDraft } from '@confluence/editor-features/entry-points/utils/getParentOfDraft.ts';
import { useWillDeleteBlankDraftOnClose } from '@confluence/editor-features/entry-points/useWillDeleteBlankDraftOnClose.ts';
import { useRestrictions } from '@confluence/editor-features/entry-points/useRestrictions.ts';
import { useEmojiTitleCoverPictureAndContentState } from '@confluence/editor-features/entry-points/useEmojiTitleCoverPictureAndContentState.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { fg } from '@confluence/feature-gating';
import { useBlogTreeActions } from '@confluence/blog-tree-store/entry-points/useBlogTreeStore.ts';
import { EDITOR_CLOSE_BUTTON_CLICK } from '@confluence/browser-metrics/entry-points/viewPageTransitionSourceConstants.ts';
import { setViewTransitionSource } from '@confluence/browser-metrics/entry-points/viewTransitionSource.ts';
import { withPageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container/entry-points/withPageTreeStateUpdater.ts';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import { useEditorObjectHeaderContext } from '../EditorObjectHeaderContext.tsx';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';
import { expValEquals } from '@confluence/feature-experiments';

type EditorCloseButtonComponentProps = {
	contentType: string;
	contentId: string;
	spaceKey: string;
	pageTreeStateUpdater: PageTreeStateUpdater;
	areMainButtonsDisabled?: boolean;
	isUnpublishedDraft?: boolean;
};

const i18n = defineMessages({
	closeButtonText: {
		id: 'content-types-header.close.name',
		defaultMessage: 'Close',
		description: 'Button text displayed on the close button in the editor header',
	},
	closeButtonPublishTooltip: {
		id: 'content-types-header.editor.close.publish.tooltip',
		defaultMessage: 'Saves changes without publishing',
		description: 'Tooltip text displayed for close button when publishing',
	},
	closeButtonUpdateTooltip: {
		id: 'content-types-header.editor.close.update.tooltip',
		defaultMessage: 'Saves changes without updating',
		description: 'Tooltip text displayed for close button when updating',
	},
	discardOnCloseButtonTooltip: {
		id: 'content-types-header.editor.discard.close.tooltip',
		defaultMessage: 'Discard empty draft',
		description:
			"Tooltip text displayed for close button if it's an empty draft and will get discarded",
	},
});

export const ANALYTICS_SOURCE = 'next-FullPageEditorComponent';

export const EditorCloseButtonComponent: FC<EditorCloseButtonComponentProps> = ({
	contentType,
	contentId,
	spaceKey,
	pageTreeStateUpdater,
	areMainButtonsDisabled = false,
	isUnpublishedDraft = false,
}) => {
	const intl = useIntl();
	const { forceRefetchBlogTree } = useBlogTreeActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { saveDraft, navigateOnClose, triggerAnalyticsEvent, version, editorActions, title } =
		useEditorObjectHeaderContext();
	const readFragmentInterop = useReadFragmentInterop({
		fragmentSpec: graphql`
			fragment EditorCloseButtonGetDraftParentFragment on Content {
				id
				ancestors {
					id
				}
				__typename
			}
		`,
	});

	// START: discardDraftOnClose logic
	const [{ collabEditProvider }] = useNativeCollabState();
	const { isSingleUserSession } = usePresence(collabEditProvider);
	const {
		hasEmoji,
		hasCoverPicture,
		hasContentState,
		emojiCoverPictureAndContentStateSubscription,
	} = useEmojiTitleCoverPictureAndContentState();
	const { hasRestrictions } = useRestrictions();
	const hasLabels = useLabels(contentId);
	const hasTitle = Boolean(title);
	const hasBody = !editorActions.isDocumentEmpty();
	const discardDraftOnClose = useWillDeleteBlankDraftOnClose({
		isUnpublishedDraft,
		isSingleUserSession,
		hasTitle,
		hasBody,
		hasEmoji,
		hasCoverPicture,
		hasContentState,
		hasRestrictions,
		hasLabels,
	});
	// END: discardDraftOnClose logic

	// START: Recent Spotlight logic
	const { setOnboardingState } = useSetOnboardingState();
	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState(
		Object.values(EDITOR_ONBOARDING_STATE_KEYS),
		!fg('cc_onboarding_experience') || version !== 1,
	);
	const { editorOnboardingEligiblity, recentSpotlightCompleted } =
		deserializeState(onboardingStateData);
	const shouldShowRecentSpotlight =
		!onboardingStateLoading && editorOnboardingEligiblity && !recentSpotlightCompleted; // Makes sure recentSpotlightCompleted is not undefined
	// END: Recent Spotlight logic

	const onClose = async () => {
		if (contentType === 'page') {
			setViewTransitionSource(EDITOR_CLOSE_BUTTON_CLICK);
		}
		if (shouldShowRecentSpotlight) {
			void setOnboardingState({
				key: EDITOR_ONBOARDING_STATE_KEYS.RECENT_SPOTLIGHT_OPEN,
				value: 'true',
			});
		}

		if (discardDraftOnClose) {
			void triggerAnalyticsEvent('close', {
				discarded: true,
			});
			void deleteDraft({
				contentId,
				deleteOnlyIfBlank: true,
			}).then(async ({ data }) => {
				if (contentType === 'blogpost') {
					// refreshes blog tree on discard close of blog draft
					forceRefetchBlogTree();
				}
				void triggerAnalyticsEvent('draftDiscarded', {
					wasDeleted: !!data?.deleteContent?.wasDeleted,
				});
				const parentId = await getParentOfDraft(contentId, readFragmentInterop);
				if (parentId) {
					pageTreeStateUpdater.setForceFetchPageId(parentId);
				} else {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'notFound',
							actionSubject: 'discardedDraftParent',
							objectType: contentType,
							objectId: contentId,
						},
					}).fire();
				}
			});
			navigateOnClose({ isDiscarding: true });
		} else {
			void triggerAnalyticsEvent('close');
			void saveDraft({
				isOnClose: true,
			}).then(() => navigateOnClose());
		}
	};

	const getCloseButtonTooltip = (): string | undefined => {
		if (areMainButtonsDisabled) {
			return undefined;
		} else if (discardDraftOnClose) {
			return intl.formatMessage(i18n.discardOnCloseButtonTooltip);
		} else if (isUnpublishedDraft) {
			return intl.formatMessage(i18n.closeButtonPublishTooltip);
		} else {
			return intl.formatMessage(i18n.closeButtonUpdateTooltip);
		}
	};

	return (
		<Fragment>
			{emojiCoverPictureAndContentStateSubscription}
			<TwoStageHydration
				name="close-button"
				// cc_editor_two_stage_hydration_removal - When cleaning up this experiment, also remove the related TwoStageHydration component
				enabled={!expValEquals('cc_editor_two_stage_hydration_removal', 'editorCloseButton', true)}
			>
				<PreloadButtonWrapper
					isPublish={false}
					contentId={contentId}
					spaceKey={spaceKey}
					contentType={contentType}
					isUnpublishedDraft={isUnpublishedDraft}
				>
					<Tooltip content={getCloseButtonTooltip()}>
						<Button
							id="close-button"
							appearance="subtle"
							onClick={onClose}
							isDisabled={areMainButtonsDisabled}
							testId="close-button"
						>
							{intl.formatMessage(i18n.closeButtonText)}
						</Button>
					</Tooltip>
				</PreloadButtonWrapper>
			</TwoStageHydration>
		</Fragment>
	);
};

export const EditorCloseButton: FC<Omit<EditorCloseButtonComponentProps, 'pageTreeStateUpdater'>> =
	withPageTreeStateUpdater(EditorCloseButtonComponent);
